import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Reveal from '../../../shared/Reveal/Reveal';
import { submitContactMessage } from '../../../../api/contactSubmissionsApi';
import { phoneCodeOptions } from '../../../../data/countries';
import { validateContactForm } from '../../../../utils/validateContactForm';
import { useContactFormContent } from '../../../../hooks/useContactFormContent';
import './Form.css';

const FieldError = ({ message }) =>
  message ? <p className="contact-form__field-error">{message}</p> : null;

const Form = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [countryCode, setCountryCode] = useState('US');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { content, isLoading } = useContactFormContent();

  const inputClass = (field) =>
    `contact-form__input${fieldErrors[field] ? ' contact-form__input--error' : ''}`;

  const clearFieldError = (field) => {
    setFieldErrors((current) => {
      if (!current[field]) {
        return current;
      }

      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const getFormValues = () => ({
    firstName,
    lastName,
    email,
    countryCode,
    phone,
    message,
    agreed,
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError('');

    const values = getFormValues();
    const validation = validateContactForm(values);

    if (!validation.isValid) {
      setFieldErrors(validation.errors);
      setFormError('Please fix the highlighted fields and try again.');
      return;
    }

    setFieldErrors({});
    setIsSubmitting(true);

    try {
      await submitContactMessage({
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        email: values.email.trim(),
        countryCode: values.countryCode.trim(),
        phone: values.phone.trim(),
        message: values.message.trim(),
        agreedToPrivacy: values.agreed,
      });

      setIsSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      if (error.errors?.length) {
        setFormError(error.errors.join(' '));
      } else {
        setFormError(error.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading || !content) {
    return null;
  }

  const { eyebrow, title, text, buttonText, privacy, success } = content;

  if (isSuccess) {
    return (
      <section className="contact-form">
        <div className="contact-form__inner">
          <Reveal className="contact-form__intro">
            {success.eyebrow ? <p className="contact-form__eyebrow">{success.eyebrow}</p> : null}
            <h2 className="contact-form__title">{success.title}</h2>
            {success.text ? <p className="contact-form__text">{success.text}</p> : null}
          </Reveal>
        </div>
      </section>
    );
  }

  return (
    <section className="contact-form">
      <div className="contact-form__inner">
        <Reveal className="contact-form__intro">
          {eyebrow ? <p className="contact-form__eyebrow">{eyebrow}</p> : null}
          <h2 className="contact-form__title">{title}</h2>
          {text ? <p className="contact-form__text">{text}</p> : null}
        </Reveal>

        <Reveal className="contact-form__panel" delay={80}>
          <form className="contact-form__form" onSubmit={handleSubmit} noValidate>
            {formError ? (
              <p className="contact-form__form-error" role="alert">
                {formError}
              </p>
            ) : null}

            <div className="contact-form__row contact-form__row--split">
              <div className="contact-form__field">
                <label className="contact-form__label" htmlFor="first-name">
                  First name
                </label>
                <input
                  id="first-name"
                  className={inputClass('firstName')}
                  type="text"
                  name="firstName"
                  value={firstName}
                  onChange={(event) => {
                    setFirstName(event.target.value);
                    clearFieldError('firstName');
                  }}
                  placeholder="First name"
                  autoComplete="given-name"
                />
                <FieldError message={fieldErrors.firstName} />
              </div>

              <div className="contact-form__field">
                <label className="contact-form__label" htmlFor="last-name">
                  Last name
                </label>
                <input
                  id="last-name"
                  className={inputClass('lastName')}
                  type="text"
                  name="lastName"
                  value={lastName}
                  onChange={(event) => {
                    setLastName(event.target.value);
                    clearFieldError('lastName');
                  }}
                  placeholder="Last name"
                  autoComplete="family-name"
                />
                <FieldError message={fieldErrors.lastName} />
              </div>
            </div>

            <div className="contact-form__field">
              <label className="contact-form__label" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                className={inputClass('email')}
                type="email"
                name="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  clearFieldError('email');
                }}
                placeholder="you@example.com"
                autoComplete="email"
              />
              <FieldError message={fieldErrors.email} />
            </div>

            <div className="contact-form__field">
              <label className="contact-form__label" htmlFor="phone">
                Phone number
              </label>
              <div className="contact-form__phone">
                <select
                  className={`contact-form__country${
                    fieldErrors.countryCode ? ' contact-form__input--error' : ''
                  }`}
                  name="countryCode"
                  value={countryCode}
                  onChange={(event) => {
                    setCountryCode(event.target.value);
                    clearFieldError('countryCode');
                  }}
                  aria-label="Country code"
                >
                  {phoneCodeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.value}
                    </option>
                  ))}
                </select>
                <input
                  id="phone"
                  className={`${inputClass('phone')} contact-form__input--phone`}
                  type="tel"
                  name="phone"
                  value={phone}
                  onChange={(event) => {
                    setPhone(event.target.value);
                    clearFieldError('phone');
                  }}
                  placeholder="+1 (555) 000-0000"
                  autoComplete="tel"
                />
              </div>
              <FieldError message={fieldErrors.countryCode || fieldErrors.phone} />
            </div>

            <div className="contact-form__field">
              <label className="contact-form__label" htmlFor="message">
                Message
              </label>
              <textarea
                id="message"
                className={`contact-form__textarea${
                  fieldErrors.message ? ' contact-form__input--error' : ''
                }`}
                name="message"
                rows={5}
                value={message}
                onChange={(event) => {
                  setMessage(event.target.value);
                  clearFieldError('message');
                }}
                placeholder="Briefly describe your request, timeframe, and preferred way to reach you."
              />
              <FieldError message={fieldErrors.message} />
            </div>

            <label className="contact-form__consent">
              <input
                className="contact-form__checkbox"
                type="checkbox"
                name="privacyPolicy"
                checked={agreed}
                onChange={(event) => {
                  setAgreed(event.target.checked);
                  clearFieldError('privacyPolicy');
                }}
              />
              <span className="contact-form__consent-text">
                {privacy.text}{' '}
                <Link to={privacy.linkUrl} className="contact-form__consent-link">
                  {privacy.linkText}
                </Link>
                .
              </span>
            </label>
            <FieldError message={fieldErrors.privacyPolicy} />

            <button
              type="submit"
              className="contact-form__submit luxury-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending…' : buttonText}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default Form;
